import { useState, useEffect } from "react";
import axios from "axios";
import MainLoader from "../../components/loading/MainLoader";
import CustomBreadcrumb from "../../components/customBreadcrumb/CustomBreadcrumb";
import Title from "../../components/title/Title";
import ServiceLineUpCard from "../../components/serviceLineUp/ServiceLineUpCard";
import { Box, Button, Grid, Typography } from "@mui/material";
import BarChartIcon from "@mui/icons-material/BarChart";
import {
  AddToPhotos,
  DataUsage,
  ShoppingCart,
} from "@mui/icons-material";
import { getCustomerReferenceNumber } from "../../services/localStorage";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../const/URL";
import { useSelector } from "react-redux";
import { IRootState } from "../../store/configureStore";
import hasPrivilege from "../../util/hasPermission";
import PRIVILEGES from "../../const/privilegesList";

interface ProductData {
  productName: string;
  displayProductName: string;
  circuitCount: number;
  relocation: string;
  modification: string;
  usageReports: string;
}

const ServiceLineUpPage = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<ProductData[]>([]);

  const { privileges, user } = useSelector(({ privileges }: IRootState) => ({
    privileges: privileges.dataBundle,
    user: privileges.dataBundleUser,
  }));

  // Check if user role is one of: USER_IT, USER_BILLING, or USER
  const restrictedRoles = ["USER_IT", "USER_BILLING", "USER"];
  const isRestrictedRole = user?.role?.code
    ? restrictedRoles.includes(user.role.code)
    : false;

  // Only check privilege if user has a restricted role
  // If hasServiceLineupAccess is true, it means the privilege EXISTS, so button should be ENABLED
  // If hasServiceLineupAccess is false, it means the privilege does NOT exist, so button should be DISABLED
  // For non-restricted roles, always allow access (treat as having the privilege)
  const hasServiceLineupAccess = isRestrictedRole
    ? hasPrivilege(privileges, PRIVILEGES.SERVICE_LINEUP_ACCESS)
    : true;

  // Helper function to handle grid size based on data length

  const getGridSize = (dataLength: number, index: number): number => {
    // if (dataLength === 1) return 12;
    // if (dataLength === 2) return 6;
    // if (dataLength % 2 === 0 && index === dataLength - 1) return 12;
    return 3;
  };

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("enterprise_portal_access_Token");

        const requestBody = {
          CustomerAccountNumber: getCustomerReferenceNumber(),
        };

        const response = await axios.post<ProductData[]>(
          `${BASE_URL}/api/v1/ENTCustomCRMAPI/api/count-by-product-with-display-name`,
          requestBody,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const responseData = Array.isArray(response.data) ? response.data : [];
        setData(responseData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div
      className="page-container"
      // style={{
      //   backgroundImage: `
      //     linear-gradient(90deg, transparent 79%, rgba(96, 165, 250, 0.2) 79%, rgba(96, 165, 250, 0.2) 81%, transparent 81%),
      //     linear-gradient(45deg, transparent 79%, rgba(96, 165, 250, 0.2) 79%, rgba(96, 165, 250, 0.2) 81%, transparent 81%),
      //     radial-gradient(circle at 10% 20%, rgba(96, 165, 250, 0.2) 5%, transparent 5.5%),
      //     radial-gradient(circle at 90% 80%, rgba(96, 165, 250, 0.2) 5%, transparent 5.5%)
      //   `,
      //   backgroundSize: "40px 40px, 40px 40px, 200px 200px, 200px 200px",
      //   backgroundPosition: "0 0, 20px 20px, 0 0, 0 0",
      //   backgroundRepeat: "repeat",
      //   backgroundColor: "white",
      //   width: "100%",
      // }}
    >
      <MainLoader show={loading} />

      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/serviceLineUp", title: "Service Line-up" },
        ]}
      />
      <Title>Service Line-up</Title>

      <Box display="flex" sx={{ gap: 2, flexDirection: "column" }}>
        {/* Left Side: Buttons */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            padding: 2,
          }}
        >
          {/* Left-aligned buttons */}
          <Box sx={{ display: "flex", gap: 2, flexGrow: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<AddToPhotos />}
              onClick={() => navigate("/serviceLineUp/addExtraData")}
              sx={{
                background: "linear-gradient(135deg, #002b5b, #0099ff)",
                color: "#fff",
                fontSize: "1rem",
                fontWeight: "bold",
                padding: "20px",
                height: "50px",
                borderRadius: "16px",
                boxShadow: 3,
                "&:hover": {
                  background: "linear-gradient(135deg, #003973, #005aa7)",
                },
              }}
            >
              Extra GB
            </Button>

            {/* <Button
              variant="contained"
              startIcon={<Campaign />}
              sx={{
                background: "linear-gradient(135deg, #006400, #00cc00)",
                color: "#fff",
                fontSize: "1rem",
                fontWeight: "bold",
                padding: "20px",
                borderRadius: "16px",
                height: "50px",
                boxShadow: 3,
                "&:hover": {
                  background: "linear-gradient(135deg, #007a33, #00aa55)",
                },
              }}
              onClick={() => navigate("/promo")}
            >
              Promo
            </Button> */}

            <Button
              variant="contained"
              startIcon={<DataUsage />}
              sx={{
                background: "linear-gradient(135deg, #00B4EB, #0091BD)",
                color: "#fff",
                fontSize: "1rem",
                fontWeight: "bold",
                padding: "20px",
                borderRadius: "16px",
                height: "50px",
                boxShadow: 3,
                "&:hover": {
                  background:
                    "linear-gradient(135deg,rgba(0, 180, 235, 0.66),rgba(0, 145, 189, 0.71))",
                },
              }}
              onClick={() => navigate("/serviceLineUp/usageSummery")}
            >
              Usage
            </Button>
          </Box>

          {/* Right-aligned buttons */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<BarChartIcon />}
              sx={{
                background: "#04559F",
                color: "#fff",
                fontSize: "1rem",
                fontWeight: "bold",
                padding: "20px",
                borderRadius: "16px",
                height: "50px",
                boxShadow: 3,
                "&:hover": {
                  background:
                    "linear-gradient(135deg,rgba(33, 91, 109, 0.66),rgba(18, 52, 62, 0.96))",
                },
              }}
              onClick={() => navigate("/serviceLineUp/protocolReport")}
            >
              Protocol Report
            </Button>
            <Button
              variant="contained"
              startIcon={<ShoppingCart />}
              sx={{
                background: "#04559F",
                color: "#fff",
                fontSize: "1rem",
                fontWeight: "bold",
                padding: "20px",
                borderRadius: "16px",
                height: "50px",
                boxShadow: 3,
                "&:hover": {
                  background:
                    "linear-gradient(135deg,rgba(33, 91, 109, 0.66),rgba(18, 52, 62, 0.96))",
                },
              }}
              onClick={() => navigate("/serviceLineUp/purchaseHistory")}
            >
              Purchase History
            </Button>
          </Box>
        </Box>

        {/* Right Side: Cards - Max 4 per page, scrollable */}
        <Box
          sx={{
            width: "100%",
            height: "38vh",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            "&::-webkit-scrollbar": {
              width: "5px",
              height: "8px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              marginTop: "20px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgb(3, 86, 161)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
          }}
        >
          {data.length === 0 && !loading ? (
            <Typography variant="body1" sx={{ textAlign: "center", mt: 4 }}>
              No data available.
            </Typography>
          ) : (
            <Grid
              container
              spacing={2}
              sx={{
                height: "100%",
                padding: 2,
              }}
            >
              {data.map((item, index) => (
                <Grid
                  item
                  key={index}
                  xs={12}
                  sm={getGridSize(data.length, index)}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: data.length === 1 ? "100%" : "auto",
                  }}
                >
                  <ServiceLineUpCard
                    title={item.displayProductName}
                    description={`${item.circuitCount} ${
                      item.circuitCount === 1 ? "Connection" : "Connections"
                    }`}
                    navigateTo={`/serviceLineUp/${item.productName}`}
                    navigationState={{
                      relocation: item.relocation,
                      modification: item.modification,
                      usageReports: item.usageReports,
                      displayName: item.displayProductName,
                    }}
                    hasServiceLineupAccess={hasServiceLineupAccess}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default ServiceLineUpPage;
